import { useMemo } from 'react';

import { useCurrentUser } from '@/hooks/useCurrentUser';

import { useDashboardQuery } from '@/query/dashboard.query';

import { AlertsList } from './components/alerts-list';
import { CallBreakdownChart } from './components/call-breakdown';
import { CallVolumeTrendChart } from './components/call-volume';
import { Filter } from './components/filters';
import { KpiGrid } from './components/kpi-grid';
import { UpcomingCalls } from './components/upcoming-calls';
import { useDashboardFilters, useUpcomingCalls } from './hooks';

import type { CardProps } from '@/types/dashboard.type';

export default function Dashboard() {
  const { userData } = useCurrentUser();
  const { range, quick, filters, onRangeChange, onQuickChange } = useDashboardFilters();
  const { data, isLoading } = useDashboardQuery(filters);

  const cardData = useMemo<CardProps[]>(() => {
    if (!data) return [];

    return [
      {
        title: 'Total calls',
        value: data.total_calls.total.toString(),
        description: 'All call interactions',
        href: '/calls',
        trendValue: '+12%',
        trendDirection: 'up',
      },
      {
        title: 'Answered rate',
        value: `${data.answered_rate.total} %`,
        description: 'Improved response rates',
        href: '/calls',
        trendValue: '+4%',
        trendDirection: 'up',
      },
      {
        title: 'Error rate',
        value: `${data.error_rate.total} %`,
        description: 'Requires immediate attention',
        href: '/calls',
        trendValue: 'High',
        trendDirection: 'down',
        variant: 'danger',
      },
    ];
  }, [data]);

  const { upcoming } = useUpcomingCalls(data?.upcoming_calls ?? []);

  return (
    <div className="mx-auto flex max-w-6xl flex-col space-y-4 p-4 pb-8 sm:space-y-4 sm:p-6 sm:pb-12">
      {/* Welcome */}
      <div>
        <h1 className="text-4xl font-semibold text-text">Welcome, {userData.first_name}</h1>
      </div>

      {/* Filters */}
      <Filter
        range={range}
        quick={quick}
        onRangeChange={onRangeChange}
        onQuickChange={onQuickChange}
      />

      {/* KPIs */}
      <KpiGrid cards={cardData} loading={isLoading} />

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CallVolumeTrendChart loading={isLoading} data={data?.volume_trend || []} />
        <CallBreakdownChart loading={isLoading} data={data?.call_outcome_breakdown || []} />
      </div>

      {/* Upcoming & Alerts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch">
        <UpcomingCalls calls={upcoming} loading={isLoading} />
        <AlertsList alerts={data?.alerts ?? []} loading={isLoading} />
      </div>
    </div>
  );
}
